import React, { useState } from 'react';
import {
  Search,
  Table,
  Upload,
  PhoneCall,
  ShieldCheck,
  Check,
  CloudDownload,
  User,
  Key,
  KeyRound,
  LogOut,
  Settings,
  Sparkles,
  UserPlus,
  ShoppingBag,
  BookOpen,
  X
} from 'lucide-react';
import { DistributorConfig } from '../types';
import { GIFFARINE_OFFICIAL_LOGO } from '../assets/logo';

interface NavbarProps {
  distributor: DistributorConfig;
  cartCount: number;
  cartTotal: number;
  onOpenCart: () => void;
  onOpenAdmin: () => void;
  onOpenUploadBanner: () => void;
  onOpenProductSync: () => void;
  onOpenPolicy?: () => void;
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  onSelectCategory?: (category: string) => void;
  currentUser?: any;
  onOpenAuth?: () => void;
  onOpenCustomerDashboard?: () => void;
  onOpenSellerDashboard?: () => void;
  onLogout?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  distributor,
  cartCount,
  cartTotal,
  onOpenCart,
  onOpenAdmin,
  onOpenUploadBanner,
  onOpenProductSync,
  onOpenPolicy,
  searchQuery = '',
  onSearchChange,
  onSelectCategory,
  currentUser,
  onOpenAuth,
  onOpenCustomerDashboard,
  onOpenSellerDashboard,
  onLogout
}) => {
  // Check if accessed through AI Studio developer environment or with dev parameters
  const isDeveloperMode = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    const href = window.location.href.toLowerCase();
    const hostname = window.location.hostname.toLowerCase();
    
    // Check if accessed through AI Studio (e.g. pinkgiffarine-online-store.ai.studio or *.ai.studio),
    // dev preview containers (*.run.app, localhost, 127.0.0.1), or URL parameter ?dev=true / ?admin=true
    const isAiStudioDomain = hostname.includes('ai.studio') || href.includes('pinkgiffarine-online-store.ai.studio');
    const isDevHost = hostname.includes('run.app') || hostname.includes('localhost') || hostname.includes('127.0.0.1');
    const hasDevQuery = new URLSearchParams(window.location.search).has('dev') || 
                        new URLSearchParams(window.location.search).has('admin') ||
                        new URLSearchParams(window.location.search).has('developer');

    return isAiStudioDomain || isDevHost || hasDevQuery;
  }, []);

  const handleMobileSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const catalogEl = document.getElementById('product-catalog-section');
      if (catalogEl) {
        catalogEl.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
      {/* ========================================================
          MOBILE TOP HEADER & SEARCH BAR (ตำแหน่งด้านบนสุด สำหรับมือถือ)
          ======================================================== */}
      <header className="block md:hidden sticky top-0 z-50 w-full shadow-md">
        <div className="bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white px-3.5 pt-2.5 pb-2.5 shadow-lg shadow-blue-900/15 border-b border-blue-300/40">
          {/* Top Row: Official Giffarine Logo */}
          <div className="flex items-center justify-between gap-2 mb-2">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                window.history.pushState({}, '', '/');
                window.dispatchEvent(new Event('popstate'));
                onSelectCategory?.('all');
              }}
              className="flex items-center gap-2 group"
            >
              <div className="w-8 h-8 rounded-xl bg-white shadow-xs overflow-hidden flex items-center justify-center shrink-0 border border-blue-200/60">
                <img src="/icon.png" alt="Giffarine App Icon" className="w-full h-full object-contain" width="32" height="32" />
              </div>
              <img src={GIFFARINE_OFFICIAL_LOGO} alt="GIFFARINE" className="h-6 sm:h-7 w-auto object-contain drop-shadow-xs" width="120" height="28" />
            </a>
          </div>

          {/* Mobile Search Input & Search Button Form */}
          <form onSubmit={handleMobileSearchSubmit} className="relative w-full">
            <div className="bg-white/95 backdrop-blur-md rounded-full shadow-md border border-blue-200 flex items-center p-1 pl-3.5 gap-2">
              <Search className="w-4 h-4 text-[#2A59FE] shrink-0" />
              <input
                type="text"
                placeholder="ค้นหาสินค้า เช่น เลซิติน, 82023..."
                value={searchQuery}
                onChange={(e) => onSearchChange?.(e.target.value)}
                className="w-full bg-transparent text-slate-800 text-xs font-bold focus:outline-none placeholder:text-slate-400 placeholder:font-normal"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => onSearchChange?.('')}
                  className="p-1 rounded-full text-slate-400 hover:text-slate-600 active:scale-90"
                  title="ล้างคำค้นหา"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="submit"
                className="bg-gradient-to-r from-[#2A59FE] to-[#4F46E5] hover:opacity-95 text-white px-3.5 py-1.5 rounded-full font-extrabold text-xs flex items-center gap-1 shadow-xs active:scale-95 shrink-0 cursor-pointer"
              >
                <Search className="w-3.5 h-3.5" />
                <span>ค้นหา</span>
              </button>
            </div>
          </form>
        </div>
      </header>

      {/* ========================================================
          DESKTOP HEADER (พื้นที่ Header ทรงสี่เหลี่ยมผืนผ้า Rectangular Design)
          ======================================================== */}
      <header className="hidden md:block sticky top-0 z-40 w-full shadow-md">
        {/* Main Royal Blue Gradient Rectangular Header Bar (+50px height adjustment) */}
        <div className="relative w-full bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white border-b border-blue-300/30 shadow-md shadow-blue-900/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-9 sm:py-10 min-h-[122px] grid grid-cols-12 items-center gap-4">
            {/* Left Column (4 cols): Official Giffarine Brand Logo */}
            <div className="col-span-4 flex items-center justify-start">
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  window.history.pushState({}, '', '/');
                  window.dispatchEvent(new Event('popstate'));
                  onSelectCategory?.('all');
                }}
                className="flex items-center gap-3.5 group"
                title="Giffarine ออนไลน์"
              >
                <div className="w-12 h-12 sm:w-13 sm:h-13 rounded-2xl bg-white shadow-md overflow-hidden flex items-center justify-center shrink-0 border border-blue-200/60 transition-transform group-hover:scale-105">
                  <img
                    src="/icon.png"
                    alt="Giffarine Official App Icon"
                    className="w-full h-full object-contain p-0.5"
                    width="48"
                    height="48"
                  />
                </div>
                {/* Embedded Official Brand Typography */}
                <img
                  src={GIFFARINE_OFFICIAL_LOGO}
                  alt="GIFFARINE"
                  className="h-10 sm:h-11 w-auto object-contain transition-transform group-hover:scale-105 drop-shadow-xs"
                  width="165"
                  height="44"
                />
              </a>
            </div>

            {/* Center Column (4 cols): Centered Desktop Search Bar */}
            <div className="col-span-4 flex items-center justify-center">
              <form onSubmit={handleMobileSearchSubmit} className="relative w-full max-w-md">
                <div className="bg-white/95 backdrop-blur-md rounded-full shadow-xs border border-blue-200/80 flex items-center p-1.5 pl-4 gap-2 transition-all focus-within:border-white focus-within:ring-2 focus-within:ring-white/40">
                  <Search className="w-4 h-4 text-[#2A59FE] shrink-0" />
                  <input
                    type="text"
                    placeholder="ค้นหาสินค้า เช่น เลซิติน, 82023..."
                    value={searchQuery}
                    onChange={(e) => onSearchChange?.(e.target.value)}
                    className="w-full bg-transparent text-slate-800 text-xs sm:text-[13px] font-bold focus:outline-none placeholder:text-slate-400 placeholder:font-normal"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => onSearchChange?.('')}
                      className="p-1 rounded-full text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                      title="ล้างคำค้นหา"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-[#2A59FE] to-[#4F46E5] hover:opacity-95 text-white px-3.5 py-1.5 rounded-full font-bold text-xs flex items-center gap-1 shadow-xs active:scale-95 shrink-0 cursor-pointer"
                  >
                    <Search className="w-3.5 h-3.5" />
                    <span>ค้นหา</span>
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column (4 cols): Cart Button & Authentication Controls */}
            <div className="col-span-4 flex items-center justify-end gap-3 sm:gap-3.5">
              {/* Circular Cart Button with Shimmering Pearl White Luster */}
              <button
                onClick={onOpenCart}
                id="header-cart-button"
                className="pearl-white-button relative w-11 h-11 sm:w-12 sm:h-12 rounded-full text-[#2A59FE] hover:text-[#1E3A8A] flex items-center justify-center transition-all hover:scale-105 active:scale-95 cursor-pointer group shadow-md border border-white/80"
                title="เปิดตะกร้าสินค้า"
              >
                <span className="text-base sm:text-lg font-black leading-none drop-shadow-xs transition-transform group-hover:scale-110 select-none">
                  {cartCount}
                </span>
              </button>

              {/* User Authentication Control */}
              {currentUser ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={currentUser.role === 'admin' || currentUser.role === 'seller' ? onOpenSellerDashboard : onOpenCustomerDashboard}
                    className="px-4 py-2.5 bg-white text-blue-900 hover:bg-blue-50 font-extrabold text-xs sm:text-[13px] rounded-full shadow-xs border border-blue-200 flex items-center gap-2 transition-all active:scale-95 cursor-pointer"
                    title="แดชบอร์ดสมาชิกกิฟฟารีน"
                  >
                    <User className="w-4 h-4 text-[#2A59FE]" />
                    <span>
                      {currentUser.role === 'admin' ? 'แดชบอร์ดแอดมิน' : currentUser.role === 'seller' ? 'แดชบอร์ดผู้ขาย (Seller)' : `แดชบอร์ดสมาชิก (ID: ${currentUser.customerId || 'ไม่มี'})`}
                    </span>
                  </button>
                  <button
                    onClick={onLogout}
                    className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border border-white/20 transition-transform active:scale-95 cursor-pointer"
                    title="ออกจากระบบ"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={onOpenAuth}
                  className="px-5 sm:px-6 py-2 sm:py-2.5 rounded-full bg-white hover:bg-slate-50 text-[#1E293B] hover:text-[#0E2C58] font-extrabold text-xs sm:text-[13.5px] flex items-center gap-2 transition-all hover:scale-102 active:scale-95 cursor-pointer shadow-md shadow-blue-950/20 border border-white/95 group"
                  title="เข้าสู่ระบบ / สมัครสมาชิก"
                >
                  <KeyRound className="w-4 h-4 text-[#2A59FE] shrink-0 transition-transform group-hover:rotate-12" />
                  <span className="tracking-tight whitespace-nowrap">เข้าสู่ระบบ / สมัครสมาชิก</span>
                </button>
              )}
            </div>
          </div>
        </div>

      {/* Subheader Navigation Links - Translucent Glassmorphism Design */}
      <div className="bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-xs transition-all">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-3.5 min-h-[50px] flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          {/* Main Category / Navigation Links */}
          <nav className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-1.5 text-sm sm:text-[14.5px] font-extrabold text-[#0E2C58]">
            <button
              onClick={() => {
                window.history.pushState({}, '', '/');
                window.dispatchEvent(new Event('popstate'));
                onSelectCategory?.('all');
              }}
              className="hover:text-blue-600 hover:bg-blue-50/70 px-3 py-1 rounded-full transition-all"
            >
              หน้าแรก
            </button>
            <button
              onClick={() => onSelectCategory?.('all')}
              className="hover:text-blue-600 hover:bg-blue-50/70 px-3 py-1 rounded-full transition-all"
            >
              โปรโมชั่น
            </button>
            <button
              onClick={() => onSelectCategory?.('beauty')}
              className="hover:text-blue-600 hover:bg-blue-50/70 px-3 py-1 rounded-full transition-all"
            >
              สินค้าใหม่
            </button>
            <button
              onClick={() => onSelectCategory?.('health')}
              className="hover:text-blue-600 hover:bg-blue-50/70 px-3 py-1 rounded-full transition-all"
            >
              สินค้าขายดี
            </button>
            <button
              onClick={() => onSelectCategory?.('all')}
              className="hover:text-blue-600 hover:bg-blue-50/70 px-3 py-1 rounded-full transition-all"
            >
              สินค้าทั้งหมด
            </button>
            {!currentUser && (
              <button
                onClick={() => {
                  window.history.pushState({}, '', '/member-privilege');
                  window.dispatchEvent(new Event('popstate'));
                }}
                className="hover:text-blue-700 hover:bg-blue-50/70 px-3 py-1 rounded-full transition-all flex items-center gap-1 font-extrabold text-[#0E2C58] hover:text-[#2A59FE]"
                title="โอกาสทางธุรกิจและสมัครสมาชิกกิฟฟารีน"
              >
                <UserPlus className="w-3.5 h-3.5 text-[#2A59FE]" />
                <span>สมัครกิฟฟารีน</span>
              </button>
            )}
            {(currentUser?.email === 'giffarine108037654@gmail.com' || currentUser?.email === 'dlgiffarine@gmail.com') && (
              <button
                onClick={() => {
                  window.history.pushState({}, '', '/memberroom');
                  window.dispatchEvent(new Event('popstate'));
                }}
                className="hover:text-blue-700 hover:bg-blue-50/70 px-3 py-1 rounded-full transition-all flex items-center gap-1 font-extrabold text-[#0E2C58] hover:text-[#2A59FE]"
                title="แนะนำสมาชิก"
              >
                <BookOpen className="w-3.5 h-3.5 text-[#2A59FE]" />
                <span>แนะนำสมาชิก</span>
              </button>
            )}
          </nav>

          {/* Sub-actions (Sync & Upload Banner - Preserved from Image 2) */}
          <div className="flex flex-wrap items-center gap-2.5 text-sm sm:text-[14px] text-slate-700">
            {/* Developer / Admin Tools */}
            {isDeveloperMode && (
              <>
                {/* Direct Seller / Admin Dashboard Button */}
                <button
                  onClick={onOpenSellerDashboard}
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-gradient-to-r from-[#0E2C58] to-[#1E3A8A] hover:opacity-95 text-white font-bold rounded-xl text-xs sm:text-[13.5px] transition-all shadow-xs active:scale-95"
                  title="เปิดแดชบอร์ดผู้ขาย / จัดการระบบร้านค้า"
                >
                  <Settings className="w-4 h-4 text-blue-200" />
                  <span>แดชบอร์ดผู้ขาย / จัดการระบบ</span>
                </button>

                {/* Quick Sync Product Button */}
                <button
                  onClick={onOpenProductSync}
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white hover:bg-slate-50 text-blue-700 font-bold rounded-xl border border-blue-200 text-xs sm:text-[14px] transition-colors shadow-2xs"
                  title="ดึงข้อมูลสินค้ากิฟฟารีนและแคชภาพฝังลงในระบบ (One-Time Sync)"
                >
                  <CloudDownload className="w-4 h-4 text-blue-600" />
                  <span>ดึงข้อมูลสินค้า (Sync)</span>
                </button>

                {/* Quick Upload Banner Button */}
                <button
                  onClick={onOpenUploadBanner}
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-xl border border-slate-200 text-xs sm:text-[14px] transition-colors shadow-2xs"
                  title="อัปโหลดหรือแก้ไขภาพสไลด์แบนเนอร์ส่วนบน"
                >
                  <Upload className="w-4 h-4 text-blue-600" />
                  <span>อัปโหลดแบนเนอร์</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  </>
);
};
